import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../styles/pages/gallery_page.css";
import Header from "../components/Header.jsx";
import Loader from "../components/Loader";
import DeleteIcon from "../assets/icons/delete-white-icon.svg";

import { fetchGallery, updateGallery } from "../services/galleryService";

import { IMAGE_BASE_URL } from "../services/imageConfig";


const GalleryPage = () => {
  const [images, setImages] = useState([null, null, null]);
  const [galleryId, setGalleryId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const loadGallery = async () => {
      setIsLoading(true);
      try {
        const data = await fetchGallery();
        if (data.length > 0) {
          setGalleryId(data[0].id);
          const imageUrls = data[0].pathToImages;

          const loadedImages = await Promise.all(
            imageUrls.map(async (url, index) => {
              try {
                const fullUrl = `${IMAGE_BASE_URL}${url}`;
                const response = await fetch(fullUrl);

                if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

                const blob = await response.blob();
                return {
                  preview: URL.createObjectURL(blob),
                  path: url,
                  name: `image-${index}.jpg`,
                  type: blob.type,
                  changed: false
                };
              } catch (error) {
                console.error("Ошибка загрузки изображения:", error);
                return null;
              }
            })
          );

          setImages(loadedImages);
        }
      } catch (error) {
        console.error("Ошибка загрузки галереи:", error);
        if (error.response?.status === 403) {
          toast.error("Время сессии истекло, пожалуйста войдите заново", {
            position: "bottom-left",
            autoClose: 3000
          });
        } else {
          toast.error("Не удалось загрузить галерею", {
            position: "bottom-left",
            autoClose: 3000
          });
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadGallery();
  }, []);

  useEffect(() => {
    return () => {
      images.forEach((file) => {
        if (file?.preview) URL.revokeObjectURL(file.preview);
      });
    };
  }, [images]);

  const handleDrop = useCallback((index, acceptedFiles) => {
    if (acceptedFiles?.[0]) {
      const file = acceptedFiles[0];
      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
        changed: true
      });

      setImages((prev) => {
        const updated = [...prev];
        if (updated[index]?.preview) URL.revokeObjectURL(updated[index].preview);
        updated[index] = newFile;
        return updated;
      });
    }
  }, []);

  const removeImage = (index) => {
    setImages((prev) => {
      const updated = [...prev];
      if (updated[index]?.preview) URL.revokeObjectURL(updated[index].preview);
      updated[index] = null;
      return updated;
    });
  };

  const handleSave = async () => {
    if (!galleryId || images.some((img) => img === null)) {
      toast.error("Загрузите все 3 изображения", {
        position: "bottom-left",
        autoClose: 3000
      });
      return;
    }

    setIsSaving(true);

    try {
      const formData = new FormData();

      images.forEach((file, index) => {
        const fieldName = `image_${["first", "second", "third"][index]}`;
        if (file && file.changed) {
          formData.append(fieldName, file, file.name);
        } else {
          formData.append(fieldName, "null");
        }
      });

      await updateGallery(galleryId, formData);

      toast.success("Галерея успешно обновлена", {
        position: "bottom-left",
        autoClose: 3000
      });

      const newImages = images.map((file) =>
        file ? { ...file, changed: false } : null
      );
      setImages(newImages);

    } catch (error) {
      console.error("Ошибка сохранения:", error);
      if (error.response?.status === 403) {
        toast.error("Время сессии истекло, пожалуйста войдите заново", {
          position: "bottom-left",
          autoClose: 3000
        });
      } else {
        toast.error(error.response?.data?.message || "Ошибка при обновлении", {
          position: "bottom-left",
          autoClose: 3000
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const filledCount = images.filter((img) => img !== null).length;

  return (
    <div className="gallery-page">
      <Header />
      <ToastContainer />

      {isLoading ? (
        <div className="loader-wrapper">
          <Loader />
          <p className="loading-text">Загрузка галереи...</p>
        </div>
      ) : (
        <div className="gallery-page_content">
          <div className="gallery_section">
            <h3 className="section-title">
              Галерея на странице "О компании"
            </h3>
            <p className="gallery-counter">
              Изображений: {filledCount}/3
            </p>
            <p className="gallery-hint">Максимальный размер для каждого изображения: 10MB</p>
            <div className="gallery-cells-container">
              {[0, 1, 2].map((index) => (
                <DropZoneCell
                  key={index}
                  index={index}
                  file={images[index]}
                  onDrop={handleDrop}
                  onRemove={removeImage}
                />
              ))}
            </div>
            <div className="gallery-button-wrapper">
              <div className="gallery-button-wrapper">
                <button
                  className="gallery-save-button"
                  onClick={handleSave}
                  disabled={!galleryId || images.includes(null) || isSaving}
                >
                  Сохранить
                </button>
              </div>

              {isSaving && (
                <div className="loader-overlay">
                  <Loader />
                  <p>Сохранение галереи...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DropZoneCell = ({ index, file, onDrop, onRemove }) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => onDrop(index, acceptedFiles),
    accept: "image/*"
  });

  return (
    <div className="gallery-cell" {...getRootProps()}>
      <input {...getInputProps()} />
      {file ? (
        <div className="gallery-cell-content">
          <img
            src={file.preview || `${IMAGE_BASE_URL}${file.path}`}
            alt={`Preview ${index}`}
            className="gallery-image"
            onError={(e) => {
              e.target.src = `${IMAGE_BASE_URL}/app/upload-dir/about_company/default-image.jpg`;
            }}
          />
          <button
            type="button"
            className="delete-button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(index);
            }}
          >
            <img src={DeleteIcon} alt="Удалить" />
          </button>
        </div>
      ) : (
        <div className="placeholder-text">
          {isDragActive
            ? "Отпустите изображение"
            : "Нажмите или перетащите изображение"}
        </div>
      )}
    </div>
  );
};

export default GalleryPage;
